import React, { useState, useEffect } from 'react';
import { DatePicker, Button, Spin, message, Tooltip } from 'antd';
import { PrinterOutlined, FileExcelOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../services/api';

const { MonthPicker } = DatePicker;
const COMPANY = '上海市闵行区智厨云食品店';

/* ============================================================
 * 标准科目骨架 — 与税务局报表格式对齐
 * ============================================================ */

// 左侧资产
const ASSET_LINES = [
  { cat: '流动资产：', indent: 0, type: 'cat' },
  { code: '1001', name: '库存现金', indent: 1 },
  { code: '1002', name: '银行存款', indent: 1 },
  { code: '1012', name: '其他货币资金', indent: 1 },
  { code: '', name: '流动资产合计', indent: 0, type: 'total', isTotal: true },
  { cat: '非流动资产：', indent: 0, type: 'cat' },
  { code: '1601', name: '固定资产原价', indent: 1 },
  { code: '1602', name: '减：累计折旧', indent: 1, negative: true },
  { code: '1603', name: '固定资产减值准备', indent: 1 },
  { code: '', name: '固定资产账面价值', indent: 1, type: 'subtotal', computed: true },
  { code: '1701', name: '无形资产', indent: 1 },
  { code: '1702', name: '减：累计摊销', indent: 1, negative: true },
  { code: '1801', name: '长期待摊费用', indent: 1 },
  { code: '1811', name: '递延所得税资产', indent: 1 },
  { code: '', name: '非流动资产合计', indent: 0, type: 'total', isTotal: true },
  { code: '', name: '资产总计', indent: 0, type: 'grand', isTotal: true },
];

// 右侧负债
const LIAB_LINES = [
  { cat: '流动负债：', indent: 0, type: 'cat' },
  { code: '2201', name: '应付票据', indent: 1 },
  { code: '2202', name: '应付账款', indent: 1 },
  { code: '2203', name: '预收账款', indent: 1 },
  { code: '2211', name: '应付职工薪酬', indent: 1 },
  { code: '2221', name: '应交税费', indent: 1 },
  { code: '2241', name: '其他应付款', indent: 1 },
  { code: '', name: '流动负债合计', indent: 0, type: 'total', isTotal: true },
  { cat: '非流动负债：', indent: 0, type: 'cat' },
  { code: '2501', name: '长期借款', indent: 1 },
  { code: '2701', name: '长期应付款', indent: 1 },
  { code: '', name: '非流动负债合计', indent: 0, type: 'total', isTotal: true },
  { code: '', name: '负债合计', indent: 0, type: 'sub', isTotal: true },
];

// 右侧权益
const EQUITY_LINES = [
  { cat: '所有者权益：', indent: 0, type: 'cat' },
  { code: '4001', name: '实收资本（或股本）', indent: 1 },
  { code: '4002', name: '资本公积', indent: 1 },
  { code: '4101', name: '盈余公积', indent: 1 },
  { code: '4103', name: '本年利润', indent: 1 },
  { code: '4104', name: '利润分配', indent: 1 },
  { code: '', name: '所有者权益合计', indent: 0, type: 'sub', isTotal: true },
  { code: '', name: '负债和所有者权益总计', indent: 0, type: 'grand', isTotal: true },
];

// 利润表标准行
const IS_LINES = [
  { cat: '一、营业收入', indent: 0, type: 'header' },
  { name: '其中：主营业务收入', indent: 1 },
  { name: '其他业务收入', indent: 1 },
  { cat: '　减：营业成本', indent: 0, type: 'header' },
  { name: '其中：主营业务成本', indent: 1 },
  { name: '其他业务成本', indent: 1 },
  { cat: '　 　　税金及附加', indent: 0, type: 'header' },
  { cat: '　 　　销售费用', indent: 0, type: 'header' },
  { cat: '　 　　管理费用', indent: 0, type: 'header' },
  { cat: '　 　　财务费用', indent: 0, type: 'header' },
  { cat: '　 　　资产减值损失', indent: 0, type: 'header' },
  { cat: '二、营业利润', indent: 0, type: 'result' },
  { name: '加：公允价值变动收益', indent: 1 },
  { name: '投资收益', indent: 1 },
  { name: '其他收益', indent: 1 },
  { cat: '三、利润总额', indent: 0, type: 'result' },
  { name: '减：所得税费用', indent: 1 },
  { cat: '四、净利润', indent: 0, type: 'grand' },
];

/* ============================================================
 * 主组件
 * ============================================================ */
export default function FinancialStatements() {
  const [activeTab, setActiveTab] = useState(0);
  const [period, setPeriod] = useState(dayjs().format('YYYY-MM'));
  const [loading, setLoading] = useState(false);
  const [bsData, setBsData] = useState<any>(null);
  const [isData, setIsData] = useState<any>(null);
  const [cfData, setCfData] = useState<any>(null);

  useEffect(() => { fetchData(); }, [period]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [bs, inc, cf] = await Promise.all([
        api({ method: 'get', url: '/finance-dashboard/reports/balance-sheet?period=' + period }).then(r => r.data?.data || r.data || r),
        api({ method: 'get', url: '/finance-dashboard/reports/income-statement?period=' + period }).then(r => r.data?.data || r.data || r),
        api({ method: 'get', url: '/finance-dashboard/reports/cash-flow?period=' + period }).then(r => r.data?.data || r.data || r),
      ]);
      setBsData(bs);
      setIsData(inc);
      setCfData(cf);
    } catch (e) {
      console.error('fetch reports error', e);
      message.error('获取报表数据失败');
    }
    setLoading(false);
  };

  /* ---------- 格式化 ---------- */
  const fmt = (v: any) => {
    if (v === null || v === undefined) return '—';
    const n = Number(v);
    if (isNaN(n) || n === 0) return '—';
    return n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const rowClass = (type: string) => {
    if (type === 'cat') return 'bs-cat';
    if (type === 'total') return 'bs-total';
    if (type === 'sub') return 'bs-sub';
    if (type === 'grand') return 'bs-grand';
    if (type === 'header') return 'is-header';
    if (type === 'result') return 'is-result';
    return '';
  };

  const periodDisplay = period ? dayjs(period).format('YYYY年MM月') : '';

  /* ==========================================================
   * 资产负债表 — 标准格式
   * ========================================================== */
  const buildBalanceRows = () => {
    if (!bsData) return [];

    // 把后端返回的平铺数组转成 code→amount 字典
    const assetMap: Record<string, number> = {};
    (bsData.assets?.current || []).forEach((a: any) => { assetMap[a.code] = Number(a.amount || 0); });
    (bsData.assets?.fixed || []).forEach((a: any) => { assetMap[a.code] = Number(a.amount || 0); });
    // 隐含货币资金放进 1002（银行存款）
    const hiddenCash = bsData._hiddenCash || 0;

    const liabMap: Record<string, number> = {};
    (bsData.liabilities?.current || []).forEach((l: any) => { liabMap[l.code] = Number(l.amount || 0); });
    (bsData.liabilities?.nonCurrent || []).forEach((l: any) => { liabMap[l.code] = Number(l.amount || 0); });

    const equityMap: Record<string, number> = {};
    (bsData.equity?.items || []).forEach((e: any) => { equityMap[e.code] = Number(e.amount || 0); });

    const totalAssets = bsData.check?.assets_total || 0;
    const totalLiab  = bsData.liabilities?.total || 0;
    // 权益合计优先用 check 反算，其次用 equity.total
    const totalEquity = bsData.equity?.total || (totalAssets - totalLiab);

    // ---- 左侧行 ----
    const leftRows: any[] = [];
    let currentAssetTotal = 0;
    let nonCurrentTotal = 0;
    let assetFinished = false;
    let liabFinished = false;
    let equityFinished = false;

    ASSET_LINES.forEach((line, idx) => {
      if (line.type === 'cat') {
        leftRows.push({ name: line.cat, type: 'cat', side: 'left' });
        return;
      }
      if (line.isTotal) {
        if (line.type === 'total' && !assetFinished) {
          // 流动资产合计
          leftRows.push({ name: line.name, amount: currentAssetTotal, type: 'total', side: 'left' });
          assetFinished = true;
          return;
        }
        if (line.type === 'total' && assetFinished && !nonCurrentTotal) {
          leftRows.push({ name: line.name, amount: nonCurrentTotal, type: 'total', side: 'left' });
          return;
        }
        if (line.type === 'grand') {
          leftRows.push({ name: line.name, amount: totalAssets, type: 'grand', side: 'left' });
          return;
        }
        return;
      }
      // 普通科目行
      let amt = assetMap[line.code] || 0;
      if (line.code === '1002') amt += hiddenCash; // 隐含现金并入银行存款
      if (line.name === '减：累计折旧' || line.name === '减：累计摊销') amt = -Math.abs(amt);
      if (line.type === 'subtotal') {
        // 固定资产账面价值 = 1601 - 1602 - 1603
        amt = (assetMap['1601'] || 0) - (assetMap['1602'] || 0) - (assetMap['1603'] || 0);
        leftRows.push({ name: line.name, amount: amt, type: 'sub', side: 'left' });
        return;
      }
      leftRows.push({ name: '　'.repeat(line.indent) + line.name, amount: amt, type: '', side: 'left' });
      if (!assetFinished) currentAssetTotal += amt;
      else nonCurrentTotal += amt;
    });

    // ---- 右侧行（负债 + 权益）----
    const rightRows: any[] = [];
    let currentLiabTotal = 0;
    let nonCurrentLiabTotal = 0;
    let equityTotal = 0;
    let phase: 'liab-curr' | 'liab-noncurr' | 'equity' = 'liab-curr';

    // 先拼负债行
    LIAB_LINES.forEach(line => {
      if (line.type === 'cat') {
        rightRows.push({ name: line.cat, type: 'cat', side: 'right' });
        return;
      }
      if (line.isTotal) {
        if (phase === 'liab-curr') {
          rightRows.push({ name: line.name, amount: currentLiabTotal, type: 'total', side: 'right' });
          phase = 'liab-noncurr';
          return;
        }
        if (phase === 'liab-noncurr') {
          rightRows.push({ name: line.name, amount: nonCurrentLiabTotal, type: 'total', side: 'right' });
          phase = 'equity';
          // 插入"负债合计"行
          rightRows.push({ name: '负债合计', amount: currentLiabTotal + nonCurrentLiabTotal, type: 'sub', side: 'right' });
          return;
        }
      }
      const amt = liabMap[line.code] || 0;
      rightRows.push({ name: '　'.repeat(line.indent) + line.name, amount: amt, type: '', side: 'right' });
      if (phase === 'liab-curr') currentLiabTotal += amt;
      else if (phase === 'liab-noncurr') nonCurrentLiabTotal += amt;
    });

    // 拼权益行
    EQUITY_LINES.forEach(line => {
      if (line.type === 'cat') {
        rightRows.push({ name: line.cat, type: 'cat', side: 'right' });
        return;
      }
      if (line.isTotal) {
        if (line.type === 'sub') {
          rightRows.push({ name: line.name, amount: totalEquity, type: 'sub', side: 'right' });
          return;
        }
        if (line.type === 'grand') {
          rightRows.push({ name: line.name, amount: totalAssets, type: 'grand', side: 'right' });
          return;
        }
      }
      const amt = equityMap[line.code] || 0;
      rightRows.push({ name: '　'.repeat(line.indent) + line.name, amount: amt, type: '', side: 'right' });
    });

    // ---- 合并成表格行 ----
    const maxLen = Math.max(leftRows.length, rightRows.length);
    const rows: any[] = [];
    for (let i = 0; i < maxLen; i++) {
      const L = leftRows[i] || { name: '', amount: undefined, type: '' };
      const R = rightRows[i] || { name: '', amount: undefined, type: '' };
      rows.push(
        <tr key={i} className={(L.type ? rowClass(L.type) : '') + ' ' + (R.type ? rowClass(R.type) : '')}>
          <td className="name">{L.name}</td>
          <td className="num">{fmt(L.amount)}</td>
          <td className="name">{R.name}</td>
          <td className="num">{fmt(R.amount)}</td>
        </tr>
      );
    }
    return rows;
  };

  /* ==========================================================
   * 利润表
   * ========================================================== */
  const buildIncomeRows = () => {
    if (!isData) return [];
    const revenue    = Number(isData.revenue    || 0);
    const cost       = Number(isData.cost       || 0);
    const tax        = Number(isData.tax        || 0);
    const e6601      = Number(isData.expense6601 || 0);
    const e6602      = Number(isData.expense6602 || 0);
    const e6603      = Number(isData.expense6603 || 0);
    const oRevenue   = Number(isData.otherRevenue  || 0);
    const oExpense   = Number(isData.otherExpense || 0);
    const opProfit   = Number(isData.operatingProfit || 0);
    const totalProfit= Number(isData.totalProfit || 0);
    const netProfit  = Number(isData.netProfit || 0);

    const rows: any[] = [];
    IS_LINES.forEach((line, idx) => {
      let current: number | undefined;
      let year: number | undefined;
      if (line.type === 'header') {
        // 标题行，无金额
        rows.push(<tr key={idx} className="is-header"><td colSpan={4} className="name">{line.cat}</td></tr>);
        return;
      }
      if (line.type === 'result') {
        if (line.cat.includes('营业利润')) current = opProfit;
        else if (line.cat.includes('利润总额')) current = totalProfit;
        rows.push(<tr key={idx} className="is-result"><td colSpan={2} className="name">{line.cat}</td><td className="num">{fmt(current)}</td><td className="num">—</td></tr>);
        return;
      }
      if (line.type === 'grand') {
        rows.push(<tr key={idx} className="is-grand"><td colSpan={2} className="name">{line.cat}</td><td className="num">{fmt(netProfit)}</td><td className="num">—</td></tr>);
        return;
      }
      // 明细行
      switch (line.name) {
        case '其中：主营业务收入': current = (isData.revenueItems||[]).find((i:any)=>i.name?.includes('主营业务'))?.amount; break;
        case '其他业务收入': current = (isData.revenueItems||[]).find((i:any)=>!i.name?.includes('主营业务'))?.amount; break;
        case '其中：主营业务成本': current = (isData.costItems||[]).find((i:any)=>i.name?.includes('主营业务'))?.amount; break;
        case '其他业务成本': current = (isData.costItems||[]).find((i:any)=>!i.name?.includes('主营业务'))?.amount; break;
        case '加：公允价值变动收益': current = (isData.fairValueChange || 0); break;
        case '投资收益': current = (isData.investmentIncome || 0); break;
        case '其他收益': current = oRevenue; break;
        case '减：所得税费用': current = (isData.incomeTax || 0); break;
        default:
          // 匹配主字段
          if (line.name.includes('营业收入') || line.name.includes('主营业务收入')) current = revenue;
          else if (line.name.includes('营业成本')) current = cost;
          else if (line.name.includes('税金及附加')) current = tax;
          else if (line.name.includes('销售费用')) current = e6601;
          else if (line.name.includes('管理费用')) current = e6602;
          else if (line.name.includes('财务费用')) current = e6603;
          else if (line.name.includes('资产减值损失')) current = (isData.assetImpairment || 0);
          else if (line.name.includes('营业外收入')) current = (isData.nonOperatingIncome || 0);
          else if (line.name.includes('营业外支出')) current = oExpense;
      }
      rows.push(
        <tr key={idx}><td className="name">{line.name}</td><td className="num">{fmt(current)}</td><td className="num">—</td></tr>
      );
    });
    return rows;
  };

  /* ==========================================================
   * 现金流量表（简化，按三大类展示）
   * ========================================================== */
  const buildCashFlowRows = () => {
    if (!cfData) return [];
    const op  = cfData.operating  || {};
    const inv = cfData.investing || {};
    const fin = cfData.financing || {};
    const LINES = [
      { cat: '一、经营活动产生的现金流量：', type: 'cat' },
      { name: '销售商品、提供劳务收到的现金', amount: op.inflow },
      { name: '购买商品、接受劳务支付的现金', amount: op.outflow },
      { name: '经营活动产生的现金流量净额', amount: op.net, type: 'result' },
      { cat: '二、投资活动产生的现金流量：', type: 'cat' },
      { name: '收回投资收到的现金', amount: inv.inflow },
      { name: '购建固定资产支付的现金', amount: inv.outflow },
      { name: '投资活动产生的现金流量净额', amount: inv.net, type: 'result' },
      { cat: '三、筹资活动产生的现金流量：', type: 'cat' },
      { name: '取得借款收到的现金', amount: fin.inflow },
      { name: '偿还债务支付的现金', amount: fin.outflow },
      { name: '筹资活动产生的现金流量净额', amount: fin.net, type: 'result' },
      { name: '现金及现金等价物净增加额', amount: cfData.netChange, type: 'grand' },
      { name: '期初现金及现金等价物余额', amount: cfData.initialBalance },
      { name: '期末现金及现金等价物余额', amount: cfData.endingBalance, type: 'grand' },
    ];
    return LINES.map((line, idx) => {
      if (line.type === 'cat') return <tr key={idx} className="cf-cat"><td colSpan={2} className="name">{line.cat}</td></tr>;
      return (
        <tr key={idx} className={line.type ? 'cf-' + line.type : ''}>
          <td className="name">{line.name}</td>
          <td className="num">{fmt(line.amount)}</td>
        </tr>
      );
    });
  };

  /* ========================================================== */
  const tabs = ['资产负债表', '利润表', '现金流量表'];

  return (
    <div className="page-container" style={{ padding: '0 0 24px' }}>
      <style>{`
        .report-header { text-align: center; margin-bottom: 12px; }
        .report-header h2 { font-size: 20px; font-weight: bold; letter-spacing: 4px; margin: 0; }
        .report-header .meta { display: flex; justify-content: space-between; font-size: 13px; margin-top: 6px; padding: 0 20px; }
        .report-wrap { background: #fff; padding: 20px 24px; border: 1px solid #d9d9d9; border-radius: 4px; overflow-x: auto; }
        .report-wrap table { width: 100%; border-collapse: collapse; font-size: 12px; table-layout: fixed; }
        .report-wrap th, .report-wrap td { border: 1px solid #000; padding: 4px 8px; vertical-align: middle; }
        .report-wrap th { background: #fafafa; font-weight: 600; text-align: center; }
        .report-wrap td.name { text-align: left; }
        .report-wrap td.num  { text-align: right; padding-right: 12px; font-family: 'Courier New', monospace; white-space: nowrap; }
        .report-wrap td.num.negative { color: #e74c3c; }
        /* 资产负债表 */
        .bs-cat td { font-weight: bold; background: #f5f5f5; }
        .bs-total td { font-weight: bold; border-top: 2px solid #000; }
        .bs-sub td { font-weight: bold; }
        .bs-grand td { font-weight: bold; border-top: 3px double #000; background: #fafafa; }
        /* 利润表 */
        .is-header td { font-weight: bold; background: #f5f5f5; }
        .is-result td { font-weight: bold; border-top: 2px solid #000; border-bottom: 2px solid #000; }
        .is-grand td { font-weight: bold; border-top: 3px double #000; }
        /* 现金流量表 */
        .cf-cat td { font-weight: bold; background: #f5f5f5; }
        .cf-result td { font-weight: bold; border-top: 2px solid #000; }
        .cf-grand td { font-weight: bold; border-top: 3px double #000; background: #fafafa; }
        .report-footer { display: flex; justify-content: space-between; margin-top: 12px; font-size: 12px; padding: 0 20px; color: #666; }
        .bar { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; flex-wrap: wrap; gap: 8px; }
        .bar .left { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
        .bar .right { display: flex; gap: 8px; }
        .tab-btns { display: flex; gap: 0; margin-bottom: -1px; }
        .tab-btn { padding: 7px 22px; background: #f0f0f0; border: 1px solid #d9d9d9; border-bottom: none; cursor: pointer; font-size: 14px; transition: all .2s; }
        .tab-btn:first-child { border-radius: 4px 0 0 0; }
        .tab-btn:last-child { border-radius: 0 4px 0 0; }
        .tab-btn.active { background: #fff; font-weight: 600; color: #1890ff; border-bottom: 2px solid #1890ff; }
        .tab-btn:hover:not(.active) { background: #e8e8e8; }
        @media print {
          .no-print { display: none !important; }
          .report-wrap { border: none; padding: 0; }
          .report-wrap table { font-size: 11px; }
        }
      `}</style>

      {/* 顶部操作栏 */}
      <div className="bar no-print">
        <div className="left">
          <h3 style={{ margin: 0, fontSize: 18 }}>财务报表</h3>
          <MonthPicker value={period ? dayjs(period) : null} onChange={(_, ds) => { if (ds) setPeriod(String(ds)); }} placeholder="选择月份" allowClear={false} />
          <Button type="primary" onClick={fetchData} icon={<span style={{ fontSize: 12 }}>⟳</span>}>查询</Button>
        </div>
        <div className="right">
          <Tooltip title="Excel导出开发中"><Button icon={<FileExcelOutlined />} disabled>导出</Button></Tooltip>
          <Tooltip title="打印当前报表"><Button icon={<PrinterOutlined />} onClick={() => window.print()}>打印</Button></Tooltip>
        </div>
      </div>

      {/* Tab 切换 */}
      <div className="tab-btns no-print">
        {tabs.map((t, i) => (
          <div key={i} className={`tab-btn ${activeTab === i ? 'active' : ''}`} onClick={() => setActiveTab(i)}>{t}</div>
        ))}
      </div>

      <Spin spinning={loading}>
        {/* ========== 资产负债表 ========== */}
        {activeTab === 0 && (
          <div className="report-wrap">
            <div className="report-header">
              <h2>资产负债表</h2>
              <div className="meta"><span>编制单位：{COMPANY}</span><span>{periodDisplay}</span><span>单位：元</span></div>
            </div>
            {bsData ? (
              <table>
                <thead>
                  <tr>
                    <th style={{ width: '38%' }}>资 产</th>
                    <th style={{ width: '12%' }}>期末余额</th>
                    <th style={{ width: '38%' }}>负债和所有者权益</th>
                    <th style={{ width: '12%' }}>期末余额</th>
                  </tr>
                </thead>
                <tbody>{buildBalanceRows()}</tbody>
              </table>
            ) : (
              <div style={{ textAlign: 'center', padding: 60, color: '#bbb' }}>暂无数据，请选择月份后点击"查询"</div>
            )}
            <div className="report-footer">
              <span>法定代表人：________</span>
              <span>主管会计工作负责人：________</span>
              <span>会计机构负责人：________</span>
            </div>
          </div>
        )}

        {/* ========== 利润表 ========== */}
        {activeTab === 1 && (
          <div className="report-wrap">
            <div className="report-header">
              <h2>利 润 表</h2>
              <div className="meta"><span>编制单位：{COMPANY}</span><span>{periodDisplay}</span><span>单位：元</span></div>
            </div>
            {isData ? (
              <table>
                <thead>
                  <tr>
                    <th style={{ width: '60%' }}>项 目</th>
                    <th style={{ width: '20%' }}>本期金额</th>
                    <th style={{ width: '20%' }}>本年累计</th>
                  </tr>
                </thead>
                <tbody>{buildIncomeRows()}</tbody>
              </table>
            ) : (
              <div style={{ textAlign: 'center', padding: 60, color: '#bbb' }}>暂无数据，请选择月份后点击"查询"</div>
            )}
            <div className="report-footer">
              <span>法定代表人：________</span>
              <span>主管会计工作负责人：________</span>
              <span>会计机构负责人：________</span>
            </div>
          </div>
        )}

        {/* ========== 现金流量表 ========== */}
        {activeTab === 2 && (
          <div className="report-wrap">
            <div className="report-header">
              <h2>现金流量表</h2>
              <div className="meta"><span>编制单位：{COMPANY}</span><span>{periodDisplay}</span><span>单位：元</span></div>
            </div>
            {cfData ? (
              <table>
                <thead>
                  <tr>
                    <th style={{ width: '70%' }}>项 目</th>
                    <th style={{ width: '30%' }}>本期金额</th>
                  </tr>
                </thead>
                <tbody>{buildCashFlowRows()}</tbody>
              </table>
            ) : (
              <div style={{ textAlign: 'center', padding: 60, color: '#bbb' }}>暂无数据，请选择月份后点击"查询"</div>
            )}
            <div className="report-footer">
              <span>法定代表人：________</span>
              <span>主管会计工作负责人：________</span>
              <span>会计机构负责人：________</span>
            </div>
          </div>
        )}
      </Spin>
    </div>
  );
}
